import { motion } from "framer-motion";
import { Code2, ShieldCheck, Palette, PenTool, ArrowUpRight, Sparkles, LayoutDashboard, Check } from "lucide-react";
import { Chapter, Reveal } from "@/components/Chapter";
import { useContent } from "@/content";

const ICONS = { Code2, ShieldCheck, Palette, PenTool };

export const Capabilities = ({ onNavigate }) => {
  const { CAPABILITIES, ADMIN_PANEL_HIGHLIGHT } = useContent();
  return (
  <section id="services" className="relative max-w-7xl mx-auto px-4 sm:px-8 py-24 md:py-32" data-testid="services-section">
    <div className="glow-blue-light absolute top-20 -right-40 h-[420px] w-[420px] rounded-full pointer-events-none" />
    <Chapter
      id="01"
      name="What we do"
      title="Every technology need, one accountable partner."
      sub="From engineering to creative production — capabilities delivered today through Desantrix, with dedicated divisions launching as we grow."
    />

    <div className="relative grid md:grid-cols-2 xl:grid-cols-4 gap-6">
      {CAPABILITIES.map((c, i) => {
        const Icon = ICONS[c.icon] || Sparkles;
        return (
          <Reveal key={c.name} delay={i * 0.1} className="h-full">
            <motion.div
              whileHover={{ y: -8 }}
              transition={{ type: "spring", stiffness: 260, damping: 20 }}
              className="group h-full rounded-2xl border border-slate-200 bg-white p-7 shadow-sm hover:shadow-xl hover:shadow-slate-900/5 hover:border-amber-400/60 transition-colors duration-500"
              data-testid={`capability-card-${i}`}
            >
              <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-amber-400/10 border border-amber-400/30 transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-6">
                <Icon className="h-6 w-6 text-amber-500" />
              </div>
              <h3 className="text-xl font-semibold text-[#0B1528] mb-1.5">{c.name}</h3>
              <p className="text-sm text-slate-500 mb-7">{c.tagline}</p>
              <ul className="space-y-3">
                {c.items.map((item, j) => (
                  <motion.li
                    key={item}
                    initial={{ opacity: 0, x: -14 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.45, delay: 0.15 + j * 0.06 }}
                    className="flex items-start gap-2.5 text-sm text-slate-600"
                  >
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-amber-400 shrink-0" />
                    {item}
                  </motion.li>
                ))}
                {c.more && (
                  <li className="flex items-start gap-2.5 text-sm text-amber-600 font-medium" data-testid="capability-more-soon">
                    <Sparkles className="h-4 w-4 mt-0.5 shrink-0" />
                    More services launching soon
                  </li>
                )}
              </ul>
            </motion.div>
          </Reveal>
        );
      })}
    </div>

    <Reveal delay={0.15} className="mt-10">
      <motion.div
        whileHover={{ y: -6 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
        className="relative overflow-hidden rounded-2xl border border-amber-300/70 bg-gradient-to-br from-amber-50 via-white to-blue-50 p-8 md:p-12 shadow-[0_24px_60px_-24px_rgba(255,159,28,0.3)]"
        data-testid="admin-panel-highlight"
      >
        <div className="glow-amber-light absolute -top-20 -right-20 h-64 w-64 rounded-full pointer-events-none" />
        <div className="relative grid md:grid-cols-[auto_1fr] gap-8 items-start">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0B1528] shadow-lg">
            <LayoutDashboard className="h-7 w-7 text-amber-400" />
          </div>
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-amber-600 mb-2">Included with every build</p>
            <h3 className="text-xl sm:text-2xl font-bold text-[#0B1528] mb-3">{ADMIN_PANEL_HIGHLIGHT.title}</h3>
            <p className="text-sm sm:text-base text-slate-600 leading-relaxed max-w-2xl mb-6">{ADMIN_PANEL_HIGHLIGHT.desc}</p>
            <ul className="flex flex-wrap gap-x-8 gap-y-3">
              {ADMIN_PANEL_HIGHLIGHT.points.map((p) => (
                <li key={p} className="flex items-center gap-2 text-sm text-slate-700">
                  <Check className="h-4 w-4 text-amber-500 shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </motion.div>
    </Reveal>

    <Reveal delay={0.2}>
      <button
        onClick={() => onNavigate("contact")}
        data-testid="services-quote-cta"
        className="mt-12 inline-flex items-center gap-2 rounded-full border border-amber-500/50 bg-white px-6 py-3 text-sm font-medium text-amber-600 hover:bg-amber-500 hover:text-white active:scale-95 transition-all duration-300"
      >
        Discuss your project
        <ArrowUpRight className="h-4 w-4" />
      </button>
    </Reveal>
  </section>
);
};
